// @ts-nocheck TODO

import {LitElement, PropertyValues, html, nothing} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";

import {citationPanelStyles} from "./CitationPanel.styles.js";
import {getFormattedCitationData, getUpdatedCitation} from "./panel_utils.js";
import {attachTooltip, TooltipInstance} from "../../common/tooltipControl.js";

import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/IconButton.js";
import "./CitationInformation.js";
import "./CitationNotes.js";

import Data from "../../../main/data/index.js";
import Integration from "../../../integrations/integration/index.js";
import {annotation, annotationCitation, citationPanelData, project} from "../../../main/data/model/index.js";

// All states show the URL, citation format, rendered citation and pages field
export const STATES = {
	STANDARD:     "citation-state-standard", // show individual fields
	SEARCH:       "citation-state-search", // show search field
	UNSTRUCTURED: "citation-state-unstructured" // hide refresh button
};

export const CITATION_CONTAINER_ID = "citation-container";

const CITATION_INFO_TAB = "citation-info-selected";
const CITATION_NOTES_TAB = "citation-notes-selected";

@customElement("pn-citation-panel")
export class CitationPanel extends LitElement {
	@property() annotation:              annotation;
	@property() citation:                annotationCitation;
	@property() editable:                boolean;
	@property() isAutoCitationsDisabled: boolean;
	@property() missingCitationsExist:   boolean;
	@property() missingUpdateResults:    object[];
	@property() notes:                   object[];
	@property() onClickPageLink:         Function; // TODO
	@property() onClickUpdateAllMissing: Function;
	@property() project:                 project;

	@state() citationFormatted: citationPanelData;
	@state() initialState = STATES.STANDARD;
	@state() selectedTab = CITATION_INFO_TAB;
	@state() state:             string;

	@query("pn-citation-information") _citationInformation: HTMLElement;
	@query(".btn-go-to-source") _sourceLinkBtn:             HTMLAnchorElement;

	// Allow skip link to work by focusing the citation panel, which will then
	// focus the first focusable element inside it
	static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

	static styles = [citationPanelStyles];

	sourceLinkTooltip: undefined | TooltipInstance;

	updated() {
		if (this._sourceLinkBtn)
			this.sourceLinkTooltip = attachTooltip(this._sourceLinkBtn);
	}

	willUpdate(changedProperties: PropertyValues<this>) {
		if (changedProperties.has("citation")) {
			// Set this.initialState as needed
			if (this.citation?.search_value && this.citation.search_value !== "")
				this.initialState = STATES.SEARCH;
			else if (!this.citation?.structured)
				this.initialState = STATES.UNSTRUCTURED;
			else
				this.initialState = STATES.STANDARD;

			this.state = this.initialState;

			// Citation data
			this.citationFormatted = getFormattedCitationData(this);
		}
	}

	setCitationInfoState = (event: CustomEvent<{state: string}>) => {
		switch (event.detail.state) {
			case "search":
				this.state = STATES.SEARCH;
				break;

			case "initial":
			default:
				this.state = this.initialState === STATES.SEARCH
					? STATES.STANDARD
					: this.initialState;
				break;
		}
	};

	closePanel() {
		const newEvent = new CustomEvent("closeCitationPanel", {
			bubbles:  true,
			composed: true
		});

		this.dispatchEvent(newEvent);
	}

	onClickCancel = () => {
		this.closePanel();
	};

	onClickSave = async () => {
		// Set loading dots, loading state

		// Get new data from citation panel
		const updatedCitation = await getUpdatedCitation(this);

		if (!Data.app.project)
			throw new Error();

		// Save citation
		Data.annotation.update(this.annotation.uuid, Data.app.project?.uuid, {citation: updatedCitation});
		this.closePanel();
	};

	onClickTabBtn(tabClicked: string) {
		if (this.selectedTab === CITATION_INFO_TAB && tabClicked === CITATION_NOTES_TAB)
			this.selectedTab = CITATION_NOTES_TAB;
		else if (this.selectedTab === CITATION_NOTES_TAB && tabClicked === CITATION_INFO_TAB)
			this.selectedTab = CITATION_INFO_TAB;
	};

	renderSourceLink() {
		// sourceUrl can be undefined, such as in the case of an AI-generated
		// freeform note
		const sourceUrl = this.annotation.pageData?.pageUrl;
		if (!sourceUrl || sourceUrl === "")
			return null;

		const isPqIntegration = Integration.isIntegration() && Integration.name === "proquest";

		const link = html`<a
			class="${classMap({
				"dotted-focus-outline": true,
				"js-btn-go-to-source":  true,
				"btn-go-to-source":     true,
				"__pq-int__":           isPqIntegration
			})}"
			data-tippy-content="Go to source"
			href="${sourceUrl}"
			@click="${(event: PointerEvent) => this.onClickPageLink(event, this.annotation.uuid)}"
			target="_blank"
		>
			<i class="icon-expand"></i>
		</a>`;

		// tippy(link); TODO

		return link;
	};

	renderMissingUpdateResultsSection() {
		// TODO: this entire section
		return nothing;

		// eslint-disable-next-line no-unreachable
		if (!this.missingUpdateResults && !(this.editable && this.missingCitationsExist))
			return nothing;

		const missingResultText = this.missingUpdateResults.citations === 0
			? "No citations were able to be updated."
			: this.missingUpdateResults.citations === 1
				? "1 citation updated."
				: this.missingUpdateResults.citations + " citations updated."
		;

		return html`
			<div className="citation-update-all-missing">
				${this.missingUpdateResults
					? html`${missingResultText}`
					: html`<div>
						To update all empty citations in this project, <a href="#" onClick={e => {
							e.preventDefault();
							props.onClickUpdateAllMissing();
						}}>click here</a>.
					</div>`
				}
			</div>
		`;
	}

	render() {
		return html`
			<div
				id="${CITATION_CONTAINER_ID}"
				class="${this.selectedTab}"
				data-annotation-uuid=${this.annotation.uuid}
			>
				<div id="tab-buttons">
					<div class="tab-spacer"></div>
					<button
						class="dotted-focus-outline"
						id="btn-citation-info"
						@click="${() => this.onClickTabBtn(CITATION_INFO_TAB)}"
					>
						Citation Information
					</button>
					<div class="tab-spacer"></div>
					<button
						class="dotted-focus-outline"
						id="btn-citation-notes"
						@click="${() => this.onClickTabBtn(CITATION_NOTES_TAB)}"
					>
						Source Notes
					</button>
					${this.renderSourceLink()}
				</div>

				${this.renderMissingUpdateResultsSection()}

				<div
					class="custom-scroll-bar-light"
					id="citation-content"
				>
					<pn-citation-information
						.citation="${this.citationFormatted}"
						.editable="${this.editable}"
						.isAutoCitationsDisabled="${this.isAutoCitationsDisabled}"
						.isRefreshAllowed="${this.editable && this.initialState !== STATES.UNSTRUCTURED}"
						@setCitationInformationState="${this.setCitationInfoState}"
						.state="${this.state}"
					></pn-citation-information>
					<pn-citation-notes
						.editable="${this.editable}"
						.notes="${this.citationFormatted?.source_notes}"
						.title="${this.citationFormatted?.title}"
					></pn-citation-notes>
				</div>

				${this.editable
					? html`<div class="citation-disclaimer">Be sure to check (and edit, if necessary) the citation above to make sure it is accurate.</div>`
					: nothing
				}

				<div id="citation-button-group">
					<pn-button
						renderType="secondary"
						.onClick="${this.onClickCancel}"
					>
						${this.editable ? "Cancel" : "Close"}
					</pn-button>

					${!this.editable
						? nothing
						: html`<pn-button .onClick="${this.onClickSave}">
							Save
						</pn-button>`
					}
				</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-citation-panel": CitationPanel;
	}
}
